/** @format */

import React, { useState, useEffect } from "react";
import { Text, Card } from "@mantine/core";
import { Message } from "tabler-icons-react";
import { showNotification } from "@mantine/notifications";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../redux/selectors";
import FileListDisplay from "./FileListDisplay";
import FileSelectionOverlay from "./FileSelectionOverlay";
import ChatSessionList from "./ChatSessionList";
import ChatManager from "../../../components/AgreementAIChat/ChatManager";
import { processConversationMultidoc } from "../../../utilities/apis/chat";
import {
  getAIAccessUrl,
  getParentFiles,
} from "../../../utilities/apis/commonApis";
import { getLink } from "../../../utilities/apis/googleDocs";
import { apiWrapWithErrorWithData } from "../../../utilities/apiHelpers";

const MAX_FILES = 10;
const STORAGE_KEY = "document_chat_sessions";

const buildStoredFileSnapshot = (file) => ({
  id: file?.id,
  name: file?.name || file?.fileName || "",
  fileName: file?.fileName || file?.name || "",
  fileType: file?.fileType || "",
  createdAt: file?.createdAt || null,
  status: file?.status || null,
  agreementData: file?.agreementData || null,
});

const buildFilesFromChatSession = (chatSession = {}) => {
  if (
    Array.isArray(chatSession.filesData) &&
    chatSession.filesData.length > 0
  ) {
    return chatSession.filesData;
  }

  if (Array.isArray(chatSession.fileIds) && chatSession.fileIds.length > 0) {
    return chatSession.fileIds.map((id, index) => ({
      id,
      fileName: chatSession.fileNames?.[index] || `agreement_${id}`,
      name: chatSession.fileNames?.[index] || `agreement_${id}`,
      agreementData: {
        json: {
          titleOfAgreement: chatSession.fileNames?.[index] || `agreement_${id}`,
        },
      },
    }));
  }

  return [];
};

const DocumentChat = () => {
  const currentUser = useSelector(selectUserData) || {};
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [chatPopupOpen, setChatPopupOpen] = useState(false);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);
  const [isProcessingChat, setIsProcessingChat] = useState(false);

  const handleRemoveFile = (fileId) => {
    setSelectedFiles(selectedFiles.filter((file) => file.id !== fileId));
  };

  const handleSelectFiles = () => {
    setOverlayOpen(true);
  };

  const handleConfirmSelection = (files) => {
    // Enforce hard cap of 10 files
    if (files.length > MAX_FILES) {
      showNotification({
        title: "Too many files selected",
        message: `You can only select up to ${MAX_FILES} documents. Please remove ${files.length - MAX_FILES} file${files.length - MAX_FILES > 1 ? "s" : ""}.`,
        color: "red",
        autoClose: 5000,
      });
      return;
    }
    setSelectedFiles(files);

    // If user was creating a new chat, open the popup after file selection
    if (isCreatingNewChat && files.length > 0) {
      createNewChatSession(files);
      setIsCreatingNewChat(false);
    }
  };

  // Create a new chat session with multi-doc API
  const createNewChatSession = async (files) => {
    setIsProcessingChat(true);

    // Validate user is logged in
    if (!currentUser?.id) {
      showNotification({
        title: "Error",
        message: "User session not found. Please refresh and try again.",
        color: "red",
      });
      setIsProcessingChat(false);
      return;
    }

    try {
      // Step 1: Get document info for each agreement

      const documentsData = [];

      // Fetch document info for each agreement
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Check if this is a legacy agreement
        const isLegacy = file.agreementData?.json?.isLegacy === true;

        if (!isLegacy) {
          showNotification({
            title: "Non-Legacy Document",
            message: `Agreement ${file.id} is a non-legacy document. Multi-document chat is currently for legacy documents only.`,
            color: "orange",
          });
          continue;
        }

        try {
          // For legacy agreements, fetch uploaded files from collaboration folder
          const filesResponse = await apiWrapWithErrorWithData(
            getParentFiles({
              parentId: file.id,
              parent: "agreement",
            }),
          );

          if (filesResponse?.success && filesResponse?.files?.length > 0) {
            // Filter for uploaded, non-errored files (same as sendLegacyDocsToChat)
            const uploadedFiles = filesResponse.files.filter(
              (f) => f.uploaded !== false && !f.errored,
            );

            if (uploadedFiles.length > 0) {
              // Use the first uploaded file (or you could handle multiple files per agreement)
              const uploadedFile = uploadedFiles[0];
              const fileUrl = uploadedFile.hostedLink || uploadedFile.url;
              const fileExtension =
                uploadedFile.extension?.toLowerCase() || "pdf";

              documentsData.push({
                agreementId: file.id,
                fileUrl: fileUrl,
                fileName:
                  uploadedFile.fileName ||
                  file.agreementData?.json?.titleOfAgreement ||
                  `agreement_${file.id}`,
                fileExtension: fileExtension,
              });
            } else {
              console.error(
                `No uploaded files found for legacy agreement ${file.id}`,
              );
              showNotification({
                title: "No Document",
                message: `Legacy agreement ${file.id} has no uploaded documents`,
                color: "orange",
              });
            }
          } else {
            console.error(`No files found for legacy agreement ${file.id}`);
            showNotification({
              title: "No Document",
              message: `Legacy agreement ${file.id} has no uploaded documents`,
              color: "orange",
            });
          }
        } catch (error) {
          console.error(
            `Error fetching files for agreement ${file.id}:`,
            error,
          );
          showNotification({
            title: "Warning",
            message: `Could not access files for agreement ${file.id}`,
            color: "orange",
          });
        }
      }

      // Check if we got at least some documents
      if (documentsData.length === 0) {
        showNotification({
          title: "Error",
          message:
            "No documents found for selected agreements. Please ensure documents are uploaded.",
          color: "red",
        });
        setIsProcessingChat(false);
        return;
      }

      if (documentsData.length < files.length) {
        showNotification({
          title: "Partial Success",
          message: `Only ${documentsData.length} of ${files.length} agreements have documents`,
          color: "yellow",
        });
      }

      // Step 2: Get signed URLs for all documents
      const signedUrlsData = [];

      for (let i = 0; i < documentsData.length; i++) {
        const doc = documentsData[i];

        try {
          let fileSignedUrl = doc.fileUrl;

          // Check if we need to get a signed URL (if not already a direct download link)
          if (fileSignedUrl.slice(0, 10) !== "https://dr") {
            const response = await apiWrapWithErrorWithData(
              getAIAccessUrl({
                hostedLink: fileSignedUrl,
                parentId: doc.agreementId,
                parent: "agreement",
              }),
            );

            if (response?.success && response?.signedUrl) {
              fileSignedUrl = response.signedUrl;
            } else {
              console.error(
                `Failed to get signed URL for agreement ${doc.agreementId}`,
              );
              continue;
            }
          }

          signedUrlsData.push({
            signedUrl: fileSignedUrl + "?download=1",
            agreementId: doc.agreementId,
            fileName: doc.fileName,
            fileExtension: doc.fileExtension,
          });
        } catch (error) {
          console.error(
            `Error getting signed URL for agreement ${doc.agreementId}:`,
            error,
          );
        }
      }

      // Final check
      if (signedUrlsData.length === 0) {
        showNotification({
          title: "Error",
          message: "Failed to access documents. Please try again.",
          color: "red",
        });
        setIsProcessingChat(false);
        return;
      }

      const signedUrls = signedUrlsData.map((d) => d.signedUrl);
      const agreementIds = signedUrlsData.map((d) => d.agreementId);
      const fileExtensions = signedUrlsData.map((d) => d.fileExtension);
      const fileNames = signedUrlsData.map((d) => d.fileName);

      // Step 3: Call processConversationMultidoc API
      const convResp = await apiWrapWithErrorWithData(
        processConversationMultidoc({
          signedUrls,
          agreementIds,
          userId: currentUser.id,
          fileExtensions,
          fileNames,
        }),
      );

      if (!convResp?.success && !convResp?.conversation_id) {
        showNotification({
          title: "Error",
          message: "Failed to create multi-document conversation",
          color: "red",
        });
        setIsProcessingChat(false);
        return;
      }

      // Step 3: Create and save chat session
      const linkedFiles = files
        .filter((file) => agreementIds.includes(file.id))
        .map((file) => buildStoredFileSnapshot(file));

      const newSession = {
        id: Date.now().toString(),
        title: `Chat with ${files.length} document${files.length > 1 ? "s" : ""}`,
        fileIds: agreementIds,
        fileNames: fileNames,
        filesData: linkedFiles,
        conversationId: convResp.conversation_id,
        initialSuggestions: convResp.suggestions || [
          "Summarize the key terms of these documents",
          "What are the main obligations in these agreements?",
          "Compare the terms across these documents",
          "Extract important dates and deadlines",
        ],
        createdAt: new Date().toISOString(),
        lastMessage: null,
      };

      // Save to localStorage
      // TODO: Replace with API call to create chat session in database
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const sessions = stored ? JSON.parse(stored) : [];
        const updated = [newSession, ...sessions];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

        // Trigger event for ChatSessionList to update
        window.dispatchEvent(new Event("chatSessionsUpdated"));
      } catch (error) {
        console.error("Error saving chat session:", error);
      }

      setSelectedFiles(linkedFiles);
      setActiveChat(newSession);
      setIsProcessingChat(false);

      showNotification({
        title: "Chat created",
        message: `New chat with ${files.length} document${files.length > 1 ? "s" : ""} created successfully`,
        color: "green",
      });
    } catch (error) {
      console.error("Error creating chat session:", error);
      showNotification({
        title: "Error",
        message: error.message || "Failed to create chat session",
        color: "red",
      });
      setIsProcessingChat(false);
    }
  };

  // Handle starting new chat flow
  const handleStartNewChat = () => {
    if (isProcessingChat) {
      showNotification({
        title: "Please wait",
        message: "Processing chat creation...",
        color: "blue",
      });
      return;
    }

    if (selectedFiles.length === 0) {
      // No files selected, prompt user to select files first
      setIsCreatingNewChat(true);
      showNotification({
        title: "Select files first",
        message: "Please select documents to start a new chat",
        color: "blue",
      });
      setOverlayOpen(true);
    } else if (selectedFiles.length > MAX_FILES) {
      showNotification({
        title: "Too many files",
        message: `You can only chat with up to ${MAX_FILES} documents at a time`,
        color: "red",
      });
    } else {
      // Files already selected, create chat immediately
      createNewChatSession(selectedFiles);
    }
  };

  // Handle selecting existing chat
  const handleChatSelect = (chatSession) => {
    const linkedFiles = buildFilesFromChatSession(chatSession);

    setSelectedFiles(linkedFiles);
    setActiveChat(chatSession);

    // Open chat popup when user clicks on a chat session
    setChatPopupOpen(true);
  };

  const handleCloseChatPopup = () => {
    setChatPopupOpen(false);
  };

  const handleDeleteChat = (sessionId) => {
    // TODO: Replace with API call to delete chat session
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const sessions = stored ? JSON.parse(stored) : [];
      const updated = sessions.filter((s) => s.id !== sessionId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error("Error deleting chat session:", error);
    }

    if (activeChat?.id === sessionId) {
      setActiveChat(null);
      setChatPopupOpen(false);
    }

    showNotification({
      title: "Chat deleted",
      message: "Chat session has been deleted",
      color: "blue",
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center mb-4">
        <Message size={28} color="#46BDE1" />
        <Text size="xl" weight={600} ml={12}>
          Document Chat
        </Text>
      </div>

      {/* Main Content - Two Column Layout */}
      <div
        className="flex gap-4 flex-1"
        style={{ height: "calc(100vh - 180px)" }}
      >
        {/* Left Panel - Chat Sessions List */}
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          style={{
            width: "40%",
            display: "flex",
            flexDirection: "column",
            minWidth: "320px",
          }}
        >
          <ChatSessionList
            onStartNewChat={handleStartNewChat}
            activeChatId={activeChat?.id}
            onChatSelect={handleChatSelect}
            onDeleteChat={handleDeleteChat}
            isProcessing={isProcessingChat}
          />
        </Card>

        {/* Right Panel - Selected Files Display */}
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <FileListDisplay
            selectedFiles={selectedFiles}
            onRemoveFile={handleRemoveFile}
            onSelectFiles={handleSelectFiles}
            activeChat={activeChat}
          />
        </Card>
      </div>

      {/* File Selection Overlay */}
      {overlayOpen && (
        <FileSelectionOverlay
          open={overlayOpen}
          onClose={() => {
            setOverlayOpen(false);
            setIsCreatingNewChat(false);
          }}
          selectedFiles={selectedFiles}
          onConfirm={handleConfirmSelection}
        />
      )}

      {/* Chat Sidebar Popup */}
      {activeChat && (
        <ChatManager
          sidebarOpen={chatPopupOpen}
          onSidebarClose={handleCloseChatPopup}
          conversationID={activeChat.conversationId}
          fileIds={activeChat.fileIds}
          initialSuggestions={
            activeChat.initialSuggestions || [
              "Summarize the key terms of these documents",
              "What are the main obligations in these agreements?",
              "Compare the terms across these documents",
              "Extract important dates and deadlines",
            ]
          }
        />
      )}
    </div>
  );
};

export default DocumentChat;
